import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import type { Instance as TippyInstance } from 'tippy.js';
import { useCallback, useState, useMemo } from 'react';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Link as LinkIcon,
  Image as ImageIcon,
  Code,
  Quote,
  Unlink,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/authStore';
import { ImageAnnotator } from './ImageAnnotator';
import { MentionList } from './MentionList';
import type { MentionUser, MentionListRef } from './MentionList';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  editable?: boolean;
  projectId?: string;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}

function ToolbarButton({ onClick, isActive, disabled, children, title }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'p-1.5 rounded-lg transition-colors',
        isActive ? 'bg-accent/20 text-accent' : 'text-text-secondary hover:text-text hover:bg-bg-tertiary',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  content,
  onChange,
  placeholder = 'Write something...',
  className,
  editable = true,
  projectId,
}: RichTextEditorProps) {
  const accessToken = useAuthStore((state) => state.accessToken);
  const [selectedImage, setSelectedImage] = useState<{ src: string; nodePos: number } | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Memoize the mention suggestion configuration
  const mentionSuggestion = useMemo(() => {
    if (!projectId || !accessToken) return undefined;

    return {
      items: async ({ query }: { query: string }): Promise<MentionUser[]> => {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/projects/${projectId}/mentionable-users?search=${encodeURIComponent(query)}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (!response.ok) {
            return [];
          }

          const result = await response.json();
          return result.data || [];
        } catch (error) {
          console.error('Failed to fetch mentionable users:', error);
          return [];
        }
      },
      render: () => {
        let component: ReactRenderer<MentionListRef> | null = null;
        let popup: TippyInstance[] | null = null;

        return {
          onStart: (props: any) => {
            component = new ReactRenderer(MentionList, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });
          },

          onUpdate(props: any) {
            component?.updateProps(props);

            if (!props.clientRect) {
              return;
            }

            popup?.[0]?.setProps({
              getReferenceClientRect: props.clientRect,
            });
          },

          onKeyDown(props: any) {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide();
              return true;
            }

            return component?.ref?.onKeyDown(props) ?? false;
          },

          onExit() {
            popup?.[0]?.destroy();
            component?.destroy();
          },
        };
      },
    };
  }, [projectId, accessToken]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-accent underline hover:text-accent-light cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-lg cursor-pointer hover:ring-2 hover:ring-accent/50 transition-all',
        },
      }),
      ...(mentionSuggestion
        ? [
            Mention.configure({
              HTMLAttributes: {
                class: 'mention',
              },
              suggestion: mentionSuggestion,
              renderHTML({ options, node }) {
                return [
                  'span',
                  {
                    'data-type': 'mention',
                    'data-id': node.attrs.id,
                    'data-label': node.attrs.label,
                    class: options.HTMLAttributes.class,
                  },
                  `@${node.attrs.label}`,
                ];
              },
            }),
          ]
        : []),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[150px] px-4 py-3',
      },
      handleClick: (view, pos, event) => {
        const target = event.target as HTMLElement;
        if (target.tagName === 'IMG' && editable) {
          const src = target.getAttribute('src');
          if (src) {
            // Find the node position of the clicked image
            const { doc } = view.state;
            let nodePos = -1;
            doc.descendants((node, position) => {
              if (node.type.name === 'image' && node.attrs.src === src) {
                nodePos = position;
                return false;
              }
              return true;
            });
            if (nodePos !== -1) {
              setSelectedImage({ src, nodePos });
            }
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) {
              uploadImage(file);
            }
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;

        for (const file of files) {
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            uploadImage(file);
            return true;
          }
        }
        return false;
      },
    },
  });

  const uploadImage = useCallback(async (file: File) => {
    if (!editor || !accessToken) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/uploads/editor-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      if (result.success && result.data?.url) {
        editor.chain().focus().setImage({ src: result.data.url }).run();
      }
    } catch (error) {
      console.error('Image upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  }, [editor, accessToken]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Enter URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  }, [editor]);

  const handleAnnotationSave = useCallback(async (annotatedImageBlob: Blob) => {
    if (!editor || !selectedImage || !accessToken) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', annotatedImageBlob, 'annotated-image.png');

      const response = await fetch(`${import.meta.env.VITE_API_URL}/uploads/editor-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      if (result.success && result.data?.url) {
        // Replace the old image with the annotated one
        const { state } = editor;
        const tr = state.tr;
        tr.setNodeMarkup(selectedImage.nodePos, undefined, {
          src: result.data.url,
        });
        editor.view.dispatch(tr);
      }
    } catch (error) {
      console.error('Annotated image upload failed:', error);
    } finally {
      setIsUploading(false);
      setSelectedImage(null);
    }
  }, [editor, selectedImage, accessToken]);

  const triggerImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        uploadImage(file);
      }
    };
    input.click();
  }, [uploadImage]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn('border border-border rounded-xl bg-bg-secondary overflow-hidden flex flex-col', className)}>
      {editable && (
        <div className="flex items-center gap-1 p-2 border-b border-border bg-bg-tertiary/50 flex-wrap flex-shrink-0">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive('heading', { level: 1 })}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Quote"
          >
            <Quote className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            title="Code Block"
          >
            <Code className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-1" />

          <ToolbarButton
            onClick={editor.isActive('link') ? removeLink : addLink}
            isActive={editor.isActive('link')}
            title={editor.isActive('link') ? 'Remove Link' : 'Add Link'}
          >
            {editor.isActive('link') ? (
              <Unlink className="w-4 h-4" />
            ) : (
              <LinkIcon className="w-4 h-4" />
            )}
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-1" />

          <ToolbarButton
            onClick={triggerImageUpload}
            disabled={isUploading}
            title="Insert Image"
          >
            <ImageIcon className="w-4 h-4" />
          </ToolbarButton>

          {isUploading && (
            <span className="text-xs text-text-muted ml-2">Uploading...</span>
          )}
        </div>
      )}

      {editor && editable && (
        <BubbleMenu
          editor={editor}
          className="flex items-center gap-1 p-1 bg-bg-tertiary border border-border rounded-lg shadow-lg"
        >
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
          >
            <Bold className="w-3.5 h-3.5" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
          >
            <Italic className="w-3.5 h-3.5" />
          </ToolbarButton>
          <ToolbarButton
            onClick={editor.isActive('link') ? removeLink : addLink}
            isActive={editor.isActive('link')}
          >
            {editor.isActive('link') ? (
              <Unlink className="w-3.5 h-3.5" />
            ) : (
              <LinkIcon className="w-3.5 h-3.5" />
            )}
          </ToolbarButton>
        </BubbleMenu>
      )}

      <div className="flex-1 overflow-auto">
        <EditorContent editor={editor} />
      </div>

      {selectedImage && (
        <ImageAnnotator
          imageSrc={selectedImage.src}
          onSave={handleAnnotationSave}
          onCancel={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
}
